import {
  CadendarConsts,
  PatientFlags,
  Transforms,
  Condition,
  RdvTypeValues,
  RdvTypeEnums,
} from "cadendar-shared";
import { BlocRuleSchema } from "cadendar-shared";
import { useEffect, useReducer, useState } from "react";
import _ from "underts";
import Select2 from "../../components/Select2";
import IconButton from "../main/components/IconButton";
import { FaMinus, FaPlus, FaArrowUp, FaArrowDown } from "react-icons/fa";
import IntegerInput from "../../components/IntegerInput";
import HMInput from "../../components/HMInput";
import { formaters } from "vincent-utils";
import { trpc } from "../main/components/trpc.ts";
import Select4 from "../../components/Select4";

// Types pour les règles de blocs avec tagged unions
type BlocRuleBase = {
  rdvType: RdvTypeValues;
  duration: number; // en minutes
  frequency: string; // "1_week", "2_week", ..., "1_day", "2_day", etc.
  timeLimit: string; // "1_day_before", "2_days_before", ..., "1_week_before", "1_month_before", etc.
  conditionsAND: Condition[]; // Support des conditions comme les autres composants
};

type BlocRule =
  | (BlocRuleBase & { timeMode: "anytime" })
  | (BlocRuleBase & { timeMode: "between"; startTime: number; endTime: number })
  | (BlocRuleBase & { timeMode: "before"; endTime: number })
  | (BlocRuleBase & { timeMode: "after"; startTime: number });

type BlocRuleState = null | BlocRule;

// Actions pour le reducer
interface ResetAction {
  type: "reset";
}

interface SetDurationAction {
  type: "setDuration";
  duration: number;
}

interface SetTimeModeAction {
  type: "setTimeMode";
  timeMode: "anytime" | "between" | "before" | "after";
  horairesExtremes?: {
    start: { hour: number; minute: number };
    end: { hour: number; minute: number };
  };
}

interface SetStartTimeAction {
  type: "setStartTime";
  time: number;
}

interface SetEndTimeAction {
  type: "setEndTime";
  time: number;
}

interface SetFrequencyAction {
  type: "setFrequency";
  frequency: string;
}

interface SetRdvTypeAction {
  type: "setRdvType";
  rdvType: RdvTypeValues;
}

interface SetTimeLimitAction {
  type: "setTimeLimit";
  timeLimit: string;
}

type Action =
  | ResetAction
  | SetDurationAction
  | SetTimeModeAction
  | SetStartTimeAction
  | SetEndTimeAction
  | SetFrequencyAction
  | SetRdvTypeAction
  | SetTimeLimitAction;

function getDefaultRule(prefs?: any): BlocRuleState {
  const defaultDuration = prefs?.duree_min_rdv || 15; // Fallback seulement pour la création de règle
  return {
    rdvType: RdvTypeEnums.PCONSULT, // Type par défaut
    duration: defaultDuration,
    timeMode: "anytime",
    frequency: "1_week", // Une fois par semaine par défaut
    timeLimit: "1_week_before", // Jusqu'à une semaine avant par défaut
    conditionsAND: [], // Pas de conditions par défaut
  };
}

function rootReducer(state: BlocRuleState, action: Action): BlocRuleState {
  switch (action.type) {
    case "reset":
      return null;
    case "setDuration":
      if (!state) return getDefaultRule();
      const newState = {
        ...state,
        duration: action.duration,
      };

      // Vérifier et ajuster les heures si nécessaire en mode "between"
      if (newState.timeMode === "between") {
        // Si endTime est trop proche de startTime avec la nouvelle durée
        if (newState.endTime < newState.startTime + action.duration) {
          newState.endTime = newState.startTime + action.duration;
        }
      }

      return newState;
    case "setTimeMode":
      if (!state) return getDefaultRule();

      // Créer une nouvelle règle selon le mode de temps
      const baseRule = {
        rdvType: state.rdvType,
        duration: state.duration,
        frequency: state.frequency,
        timeLimit: state.timeLimit,
        conditionsAND: state.conditionsAND,
      };

      // Utiliser les horaires dynamiques ou des fallbacks
      const horaires = action.horairesExtremes || {
        start: { hour: 8, minute: 0 },
        end: { hour: 18, minute: 0 },
      };

      const startMinutes = horaires.start.hour * 60 + horaires.start.minute;
      const endMinutes = horaires.end.hour * 60 + horaires.end.minute;

      switch (action.timeMode) {
        case "anytime":
          return {
            ...baseRule,
            timeMode: "anytime" as const,
          };
        case "between":
          return {
            ...baseRule,
            timeMode: "between" as const,
            startTime: startMinutes, // Heure d'ouverture
            endTime: Math.min(endMinutes, startMinutes + state.duration), // Assurer une durée valide
          };
        case "before":
          return {
            ...baseRule,
            timeMode: "before" as const,
            endTime: Math.min(endMinutes, startMinutes + state.duration), // Au moins une durée après l'ouverture
          };
        case "after":
          return {
            ...baseRule,
            timeMode: "after" as const,
            startTime: Math.max(startMinutes, endMinutes - state.duration), // Au plus une durée avant la fermeture
          };
      }
      break;
    case "setStartTime":
      if (!state) return state;
      if (state.timeMode === "between" || state.timeMode === "after") {
        return {
          ...state,
          startTime: action.time,
        };
      }
      return state;
    case "setEndTime":
      if (!state) return state;
      if (state.timeMode === "between" || state.timeMode === "before") {
        return {
          ...state,
          endTime: action.time,
        };
      }
      return state;
    case "setFrequency":
      if (!state) return state;
      return {
        ...state,
        frequency: action.frequency,
      };
    case "setRdvType":
      if (!state) return state;
      return {
        ...state,
        rdvType: action.rdvType,
      };
    case "setTimeLimit":
      if (!state) return state;
      return {
        ...state,
        timeLimit: action.timeLimit,
      };
    default:
      return state;
  }
}

interface BlocRuleLineProps {
  state: BlocRuleState;
  dispatch: (action: Action) => void;
}

const BlocRuleLine = (props: BlocRuleLineProps) => {
  const state = props.state;
  const dispatch = props.dispatch;

  const prefs = trpc.preference.get.useQuery().data;
  const horaires = trpc.horaire.byDate.useQuery({
    date: new Date(2021, 0, 1),
  }).data;
  const horairesExtremes = (horaires &&
    Transforms.horaires.horairesExtremesForWeek(horaires)) || {
    start: { hour: 0, minute: 0 },
    end: { hour: 23, minute: 59 },
  };
  const hmPossibles =
    (prefs?.duree_min_rdv &&
      Transforms.horaires.getAllHMinForPlage(
        horairesExtremes,
        prefs?.duree_min_rdv
      )) ||
    [];

  // Générer les options de durée basées sur la granularité des préférences
  const durationOptions = prefs?.duree_min_rdv
    ? (() => {
        // Calculer l'amplitude maximum de l'agenda
        const amplitudeMinutes = horairesExtremes
          ? horairesExtremes.end.hour * 60 +
            horairesExtremes.end.minute -
            (horairesExtremes.start.hour * 60 + horairesExtremes.start.minute)
          : 720; // Fallback 12h si pas d'horaires

        // Générer les options jusqu'à l'amplitude maximum
        return Array.from(
          { length: Math.floor(amplitudeMinutes / prefs.duree_min_rdv) },
          (_, i) => (i + 1) * prefs.duree_min_rdv
        )
          .filter((duration) => duration <= amplitudeMinutes)
          .map((duration) => ({
            key: duration.toString(),
            label: `${duration} min`,
          }));
      })()
    : [];

  const TimeModeSelect = (props: {
    value: "anytime" | "between" | "before" | "after";
    onChange: (value: "anytime" | "between" | "before" | "after") => void;
  }) => {
    return (
      <Select2
        selected={props.value}
        onSelect={props.onChange}
        defaultKey="anytime"
        keyMap={{
          anytime: "n'importe quand",
          between: "entre",
          before: "avant",
          after: "après",
        }}
        className=""
      />
    );
  };

  const DurationSelect = (props: {
    value: number;
    onChange: (value: number) => void;
  }) => {
    // Si pas d'options de durée disponibles, afficher un message
    if (durationOptions.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          Granularité non définie
        </div>
      );
    }

    const keyMap = durationOptions.reduce(
      (acc, opt) => ({
        ...acc,
        [opt.key]: opt.label,
      }),
      {} as Record<string, string>
    );

    const selectedKey = props.value.toString();

    // Vérifier si la valeur actuelle est dans les options disponibles
    const isValidKey = durationOptions.some((opt) => opt.key === selectedKey);
    const keyToUse = isValidKey ? selectedKey : durationOptions[0]?.key;

    return (
      <Select2
        selected={keyToUse}
        onSelect={(key: string) => props.onChange(parseInt(key))}
        defaultKey={durationOptions[0]?.key}
        keyMap={keyMap}
        className=""
      />
    );
  };

  const RdvTypeSelect = (props: {
    value: RdvTypeValues;
    onChange: (value: RdvTypeValues) => void;
  }) => {
    const handleChange = (key: typeof RdvTypeEnums.keyType) => {
      props.onChange(RdvTypeEnums[key]);
    };
    return (
      <Select4
        selected={RdvTypeEnums.key(props.value)!}
        onSelect={handleChange}
        defaultKey={"PCONSULT"}
        className=""
        items={RdvTypeEnums.items}
      />
    );
  };

  const FrequencySelect = (props: {
    value: string;
    onChange: (value: string) => void;
  }) => {
    // Calculer le nombre de jours ouverts par semaine
    const openDaysCount = horaires
      ? Object.values(horaires.horaire).filter((day) => day.length > 0).length
      : 0;

    // Générer les options de fréquence par semaine limitées par les jours ouverts
    const weeklyFrequencyOptions: Record<string, string> = {};
    for (let i = 1; i <= openDaysCount; i++) {
      const key = `${i}_week`;
      const label = `${i} fois par semaine`;
      weeklyFrequencyOptions[key] = label;
    }

    const frequencyOptions = {
      // Fréquences par semaine (limitées par les jours ouverts)
      ...weeklyFrequencyOptions,
      // Fréquence par jour (maximum une fois)
      "1_day": "1 fois par jour",
    };

    return (
      <Select2
        selected={props.value as keyof typeof frequencyOptions}
        onSelect={props.onChange}
        defaultKey="1_week"
        keyMap={frequencyOptions}
        className=""
      />
    );
  };

  const TimeLimitSelect = (props: {
    value: string;
    onChange: (value: string) => void;
  }) => {
    const timeLimitOptions = {
      // Jours avant
      "1_day_before": "jusqu'à un jour avant aujourd'hui",
      "2_days_before": "jusqu'à deux jours avant aujourd'hui",
      "3_days_before": "jusqu'à trois jours avant aujourd'hui",
      "4_days_before": "jusqu'à quatre jours avant aujourd'hui",
      "5_days_before": "jusqu'à cinq jours avant aujourd'hui",
      "6_days_before": "jusqu'à six jours avant aujourd'hui",
      // Semaines avant
      "1_week_before": "jusqu'à une semaine avant aujourd'hui",
      "2_weeks_before": "jusqu'à deux semaines avant aujourd'hui",
      "3_weeks_before": "jusqu'à trois semaines avant aujourd'hui",
      // Mois avant
      "1_month_before": "jusqu'à un mois avant aujourd'hui",
      "2_months_before": "jusqu'à deux mois avant aujourd'hui",
      "3_months_before": "jusqu'à trois mois avant aujourd'hui",
      "4_months_before": "jusqu'à quatre mois avant aujourd'hui",
      "5_months_before": "jusqu'à cinq mois avant aujourd'hui",
      "6_months_before": "jusqu'à six mois avant aujourd'hui",
    };

    return (
      <Select2
        selected={props.value as keyof typeof timeLimitOptions}
        onSelect={props.onChange}
        defaultKey="1_week_before"
        keyMap={timeLimitOptions}
        className=""
      />
    );
  };

  return (
    <div className="p-2 grid grid-cols-[auto_auto_auto_1fr_auto_auto_auto_auto_1fr_1fr] gap-3 items-center">
      <span className="font-medium">Réserver</span>

      {state && (
        <>
          <DurationSelect
            value={state.duration}
            onChange={(duration) => dispatch({ type: "setDuration", duration })}
          />

          <span className="">pour les rdvs de type</span>

          <RdvTypeSelect
            value={state.rdvType}
            onChange={(rdvType) => dispatch({ type: "setRdvType", rdvType })}
          />

          <TimeModeSelect
            value={state.timeMode}
            onChange={(timeMode) =>
              dispatch({
                type: "setTimeMode",
                timeMode,
                horairesExtremes,
              })
            }
          />

          {state.timeMode === "between" && (
            <>
              <HMInput
                hmSelected={
                  formaters.getHourMinutesFromMinutes(state.startTime) ||
                  horairesExtremes.start
                }
                handleHMChange={(hm: { hour: number; minute: number }) => {
                  const newStartTime =
                    formaters.getHourAsMinutesFromHMObject(hm);
                  dispatch({
                    type: "setStartTime",
                    time: newStartTime,
                  });
                  // Si endTime devient invalide (trop proche), la mettre à jour
                  if (state.endTime < newStartTime + state.duration) {
                    const newEndTime = newStartTime + state.duration;
                    // Vérifier que la nouvelle endTime ne dépasse pas les horaires
                    const maxEndTime =
                      horairesExtremes.end.hour * 60 +
                      horairesExtremes.end.minute;
                    if (newEndTime <= maxEndTime) {
                      dispatch({
                        type: "setEndTime",
                        time: newEndTime,
                      });
                    }
                  }
                }}
                hmPossibles={hmPossibles.filter((hm) => {
                  const hmMinutes = formaters.getHourAsMinutesFromHMObject(hm);
                  // Permettre seulement les heures qui laissent assez de place pour la durée
                  return (
                    hmMinutes + state.duration <=
                    horairesExtremes.end.hour * 60 + horairesExtremes.end.minute
                  );
                })}
              />
              <span className="text-sm">et</span>
              <HMInput
                key={`endTime-${state.startTime}-${state.duration}`} // Force le re-render quand startTime ou duration change
                hmSelected={
                  formaters.getHourMinutesFromMinutes(state.endTime) ||
                  horairesExtremes.end
                }
                handleHMChange={(hm: { hour: number; minute: number }) =>
                  dispatch({
                    type: "setEndTime",
                    time: formaters.getHourAsMinutesFromHMObject(hm),
                  })
                }
                hmPossibles={hmPossibles.filter((hm) => {
                  const hmMinutes = formaters.getHourAsMinutesFromHMObject(hm);
                  return hmMinutes >= state.startTime + state.duration;
                })}
              />
            </>
          )}

          {state.timeMode === "before" && (
            <>
              <HMInput
                hmSelected={
                  formaters.getHourMinutesFromMinutes(state.endTime) ||
                  horairesExtremes.end
                }
                handleHMChange={(hm: { hour: number; minute: number }) =>
                  dispatch({
                    type: "setEndTime",
                    time: formaters.getHourAsMinutesFromHMObject(hm),
                  })
                }
                hmPossibles={hmPossibles.filter((hm) => {
                  const hmMinutes = formaters.getHourAsMinutesFromHMObject(hm);
                  // L'heure de fin doit permettre au moins une durée complète avant elle
                  return (
                    hmMinutes >=
                    horairesExtremes.start.hour * 60 +
                      horairesExtremes.start.minute +
                      state.duration
                  );
                })}
              />
              <div></div> {/* Placeholder pour maintenir la grille */}
              <div></div> {/* Placeholder pour maintenir la grille */}
            </>
          )}

          {state.timeMode === "after" && (
            <>
              <HMInput
                hmSelected={
                  formaters.getHourMinutesFromMinutes(state.startTime) ||
                  horairesExtremes.start
                }
                handleHMChange={(hm: { hour: number; minute: number }) =>
                  dispatch({
                    type: "setStartTime",
                    time: formaters.getHourAsMinutesFromHMObject(hm),
                  })
                }
                hmPossibles={hmPossibles.filter((hm) => {
                  const hmMinutes = formaters.getHourAsMinutesFromHMObject(hm);
                  // L'heure de début doit permettre au moins une durée complète après elle
                  return (
                    hmMinutes <=
                    horairesExtremes.end.hour * 60 +
                      horairesExtremes.end.minute -
                      state.duration
                  );
                })}
              />
              <div></div> {/* Placeholder pour maintenir la grille */}
              <div></div> {/* Placeholder pour maintenir la grille */}
            </>
          )}

          {state.timeMode === "anytime" && (
            <>
              <div></div> {/* Placeholder pour maintenir la grille */}
              <div></div> {/* Placeholder pour maintenir la grille */}
              <div></div> {/* Placeholder pour maintenir la grille */}
            </>
          )}

          <FrequencySelect
            value={state.frequency}
            onChange={(frequency) =>
              dispatch({ type: "setFrequency", frequency })
            }
          />

          <TimeLimitSelect
            value={state.timeLimit}
            onChange={(timeLimit) =>
              dispatch({ type: "setTimeLimit", timeLimit })
            }
          />
        </>
      )}
    </div>
  );
};

const BlocRuleReducer = (
  state: BlocRuleState[] = [],
  action:
    | { ruleIndex: number; action: Action; type: "lineAction" }
    | { type: "addLine"; defaultRule: BlocRuleState }
    | { type: "removeLine"; lineNumber: number }
    | { type: "moveLineUp"; lineNumber: number }
    | { type: "moveLineDown"; lineNumber: number }
    | { type: "assignState"; state: BlocRuleState[] }
) => {
  if (action.type === "addLine") {
    return [...state, action.defaultRule];
  }
  if (action.type === "removeLine") {
    return [
      ...state.slice(0, action.lineNumber),
      ...state.slice(action.lineNumber + 1),
    ];
  }
  if (action.type === "moveLineUp") {
    if (action.lineNumber === 0) return state; // Déjà en haut
    const newState = [...state];
    [newState[action.lineNumber - 1], newState[action.lineNumber]] = [
      newState[action.lineNumber],
      newState[action.lineNumber - 1],
    ];
    return newState;
  }
  if (action.type === "moveLineDown") {
    if (action.lineNumber === state.length - 1) return state; // Déjà en bas
    const newState = [...state];
    [newState[action.lineNumber], newState[action.lineNumber + 1]] = [
      newState[action.lineNumber + 1],
      newState[action.lineNumber],
    ];
    return newState;
  }
  if (action.type === "lineAction") {
    return state.map((r, idx) =>
      idx === action.ruleIndex ? rootReducer(r, action.action) : r
    );
  }
  if (action.type === "assignState") {
    return action.state;
  }
  return state;
};

interface BlocsMainCompProps {
  rules: BlocRuleState[] | null;
}

export const BlocsMainComp = (props: BlocsMainCompProps) => {
  const [state, dispatch] = useReducer(BlocRuleReducer, []);
  console.log("state", state);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const prefs = trpc.preference.get.useQuery().data;

  useEffect(() => {
    if (props.rules) {
      dispatch({ type: "assignState", state: props.rules });
    }
  }, [props.rules]);

  const hasChanges = JSON.stringify(state) !== JSON.stringify(props.rules);
  const isValid = state.every(
    (rule) =>
      rule === null ||
      (rule.duration > 0 &&
        (rule.timeMode === "anytime" ||
          (rule.timeMode === "between" &&
            rule.startTime !== undefined &&
            rule.endTime !== undefined) ||
          (rule.timeMode === "before" && rule.endTime !== undefined) ||
          (rule.timeMode === "after" && rule.startTime !== undefined)))
  );

  const handleDispatch = (ruleIndex: number) => (action: Action) =>
    dispatch({ ruleIndex, action, type: "lineAction" });

  const utils = trpc.useUtils();
  const submitRulesMutation = trpc.rule.submitBlocRulesForOwner.useMutation({
    onSuccess: () => {
      utils.rule.getAll.invalidate();
    },
  });

  const handleSubmit = () => {
    const validRules = state.filter((rule): rule is BlocRule => rule !== null);

    // Validation avec Zod
    const errors: string[] = [];
    const validatedRules = validRules
      .map((rule, index) => {
        try {
          return BlocRuleSchema.parse(rule);
        } catch (error) {
          if (error instanceof Error) {
            errors.push(`Règle ${index + 1}: ${error.message}`);
          }
          return null;
        }
      })
      .filter((rule): rule is NonNullable<typeof rule> => rule !== null);

    if (errors.length > 0) {
      setValidationErrors(errors);
      console.error("Erreurs de validation des règles de bloc:", errors);
      return;
    }

    // Reset des erreurs si tout est valide
    setValidationErrors([]);

    console.log("Submitting bloc rules:", validatedRules);

    submitRulesMutation.mutate(validatedRules);
  };

  return (
    <div className="m-4">
      <div className="text-xl">Règles de blocs</div>
      <div className="m-1">
        {state.map((rule, idx) => (
          <div key={idx} className="flex items-center">
            <div className="flex gap-1">
              {idx > 0 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineUp", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le haut"
                >
                  <FaArrowUp />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
              {idx < state.length - 1 ? (
                <IconButton
                  onClick={() =>
                    dispatch({ type: "moveLineDown", lineNumber: idx })
                  }
                  tooltip="Déplacer vers le bas"
                >
                  <FaArrowDown />
                </IconButton>
              ) : (
                <div className="w-8"></div>
              )}
            </div>
            <IconButton
              onClick={() => dispatch({ type: "removeLine", lineNumber: idx })}
              tooltip="Retirer règle"
            >
              <FaMinus />
            </IconButton>
            <BlocRuleLine state={rule} dispatch={handleDispatch(idx)} />
          </div>
        ))}
        <div className="pt-2">
          <IconButton
            onClick={() =>
              dispatch({ type: "addLine", defaultRule: getDefaultRule(prefs) })
            }
            tooltip="Ajouter une règle"
          >
            <FaPlus />
          </IconButton>
        </div>
        {state.length > 0 && (
          <div className="pt-3">
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-blue-600 transition-colors"
              onClick={handleSubmit}
              disabled={!hasChanges || !isValid}
            >
              Enregistrer
            </button>
            {!isValid && hasChanges && (
              <div className="text-red-500 text-sm mt-1">
                Veuillez compléter toutes les règles avant de sauvegarder.
              </div>
            )}
            {validationErrors.length > 0 && (
              <div className="text-red-500 text-sm mt-1">
                <div className="font-semibold">Erreurs de validation :</div>
                <ul className="list-disc list-inside mt-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* TODO: Afficher les erreurs de la mutation */}
            {/* {submitRulesMutation.error && (
              <div className="text-red-500 text-sm mt-1">
                Erreur lors de la sauvegarde: {submitRulesMutation.error.message}
              </div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlocsMainComp;
